import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import DataTableBase from "../../Components/Common/DataTableBase/DataTableBase";
import { t } from "i18next";
import { useMenuStore } from "../../stores/Assets/menu";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledTooltip,
} from "reactstrap";
import AddItem from "./Components/AddItem";
import EditItem from "./Components/EditItem";
import { useLocation } from "react-router-dom";
import { useConfirmDialogStore } from "../../stores/Modal/ConfirmDialogStore";
import { useMenuItemsStore } from "../../stores/Assets/menuItems";
import { OptionsModal } from "./Components/OptionsModal";
import { Pagination } from "../../Components/Common/DataTableBase/Pagination";
import ADModal from "./Components/ADModal";
import OrderingModal from "./Components/OrderingModal";
import HandleImagesModal from "./Components/HandleImagesModal";
import BulkAddItemsModal from "./Components/BulkAddItemsModal";
import { canEdit } from "../../helpers/permissions";
import SearchBar from "../../Components/Common/SearchBar";
import { useUsageStore } from "../../stores/Usage";
import { PlanUpgradeModal } from "../../Components/Common/PlanUpgradeModal";

const Menu = () => {
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const { menuid } = useParams();

  document.title = "Clickorder Admin";
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(null);
  const [showADModal, setShowADModal] = useState(false);
  const [showOrderingModal, setShowOrderingModal] = useState(false);
  const [showHandleImagesModal, setShowHandleImagesModal] = useState(false);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const {
    getItems,
    isLoading,
    items,
    deleteItem,
    currency,
    image_url,
    menuName,
    menuNameAr,
    pagination,
  } = useMenuItemsStore((state) => state);
  const { fetchUsage, getLimitInfo } = useUsageStore();
  const itemLimit = getLimitInfo("menu_items");
  const isLimitReached = itemLimit?.isReached;

  useEffect(() => {
    fetchUsage();
  }, []);

  useEffect(() => {
    if (menuid) {
      getItems(menuid, 1, 10, searchTerm);
    }
  }, [menuid, searchTerm]);

  useEffect(() => {
    setTotalRows(pagination?.totalItems);
  }, [pagination]);

  const handleOptionsClick = (id) => {
    setSelectedOptions(id);
    setShowOptionsModal(true);
  };

  const toggleADModal = () => {
    setShowADModal(!showADModal);
  };

  const toggleOrderingModal = () => {
    setShowOrderingModal(!showOrderingModal);
  };

  const toggleHandleImagesModal = () => {
    setShowHandleImagesModal(!showHandleImagesModal);
  };

  const toggleBulkAddModal = () => {
    if (!showBulkAddModal && isLimitReached) {
      setShowUpgradeModal(true);
      return;
    }
    setShowBulkAddModal(!showBulkAddModal);
  };

  const handleOrderingUpdate = () => {
    // Refresh the menu items after ordering update
    if (menuid) {
      getItems(menuid, 1, 10, searchTerm);
    }
  };
  const columns = [
    {
      name: t("ID"),
      selector: (row) => row?.item_id,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Title"),
      selector: (row) => row?.title,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.title}</span>
        </div>
      ),
    },
    {
      name: t("Title (AR)"),
      selector: (row) => row?.title_ar,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.title_ar}</span>
        </div>
      ),
    },
    {
      name: t("Type"),
      selector: (row) => row?.type,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.type}</span>
        </div>
      ),
    },
    {
      name: t("Options"),
      selector: (row) => row?.options?.length,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="cursor-pointer" id={`anchor-${row?.item_id}`}>
          <span
            style={{ fontSize: "12px" }}
            className={`badge bg-soft-${row?.options?.length > 0 ? "info" : "warning"
              } cursor-pointer text-${row?.options?.length > 0 ? "success" : "warning"
              } text-uppercase `}
            onClick={() => handleOptionsClick(row.item_id)}
          >
            {row?.options?.length > 0 && (
              <i className="ri-external-link-line"></i>
            )}{" "}
            {row?.options?.length > 0 ? "Check" : "No Options"}
          </span>
          <UncontrolledTooltip
            placement="top"
            target={`anchor-${row?.item_id}`}
          >
            {row?.options?.length > 0
              ? "Check Associated Options"
              : "No options available"}
          </UncontrolledTooltip>
        </div>
      ),
    },
    {
      name: t("Price"),
      selector: (row) => row?.price,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <span>
          {row?.price?.toFixed(1)} {currency}
        </span>
      ),
    },
    {
      name: t("Available"),
      selector: (row) => row?.available,
      sortable: true,
      wrap: true,
      cell: (row) => <span>{row?.available ? "Yes" : "No"}</span>,
    },
    {
      name: t("Hidden"),
      selector: (row) => row?.isHidden,
      sortable: true,
      wrap: true,
      cell: (row) => <span>{row?.isHidden ? "Yes" : "No"}</span>,
    },
    {
      name: t("Image"),
      cell: (row) =>
        row?.images.length ? (
          <img
            src={image_url + row.images[0]}
            alt="Item"
            style={{ width: "50px", height: "50px", objectFit: "cover" }}
          />
        ) : (
          <span>No Image</span>
        ),
    }
  ];
  const { showConfirm } = useConfirmDialogStore((state) => state);

  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showEditItemModal, setshowEditItemModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const toggleUpgradeModal = () => setShowUpgradeModal(!showUpgradeModal);

  const toggleAddItemModal = () => {
    if (!showAddItemModal && isLimitReached) {
      setShowUpgradeModal(true);
      return;
    }
    setShowAddItemModal(!showAddItemModal);
  };

  const [selectedRow, setSelectedRow] = useState(null);
  const toggleEditItemModal = (row) => {
    setSelectedRow(row);
    setshowEditItemModal(!showEditItemModal);
  };

  const onChangePage = (page) => {
    getItems(menuid, page, 10, searchTerm);
  };

  const searchHandler = (searchText) => {
    setSearchTerm(searchText);
  };

  const deleteItemFun = async (id) => {
    try {
      await deleteItem(menuid, id);
      fetchUsage();
    } catch (e) {
      console.log(e);
    }
  };

  const [currenItems, setCurrenItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // Track the current page
  const rowsPerPage = 10; // Display 5 rows per page
  useEffect(() => {
    if (items?.length > 0) {
      const curr = items.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      );

      setCurrenItems(curr);
    }
  }, [currentPage, items]);

  const onPageChange = (page) => {
    setCurrentPage(page);
  };
  const [canEditMenuItem, setCanEditMenuItem] = useState(false);

  useEffect(() => {
    const checkPermission = async () => {
      const res = await canEdit("menu_item");
      setCanEditMenuItem(res);
    };

    checkPermission();
  }, []);
  return (
    <>
      <DataTableBase
        tableTitle={menuName ? `Menu Items - ${menuName}` : "Menu Items"}
        customHeader={
          <div className="w-100">
            <div className="d-flex flex-column align-items-stretch justify-content-between gap-2 w-100 mb-2">
              {canEditMenuItem && (
                <div className="d-flex justify-content-center align-items-center gap-2">
                  <Button
                    color={isLimitReached ? "secondary" : "success"}
                    onClick={toggleAddItemModal}
                    className="d-flex align-items-center justify-content-center w-100"
                    style={{ flex: '0 0 10%', padding: '0.4rem 2rem', whiteSpace: 'nowrap', fontSize: "13px" }}
                    id="add-item-btn"
                  >
                    + {t("Add Item")}
                  </Button>
                  {isLimitReached && (
                    <UncontrolledTooltip target="add-item-btn">
                      {t("Limit reached. Upgrade to add more.")}
                    </UncontrolledTooltip>
                  )}
                  <Button className="d-flex align-items-center justify-content-center w-100" style={{ flex: '0 0 10%', padding: '0.4rem 2rem', whiteSpace: 'nowrap', fontSize: "13px" }} color="info" onClick={toggleOrderingModal}>
                    <i className="ri-drag-move-2-line me-1"></i>
                    Reorder Items
                  </Button>
                  <Button className="d-flex align-items-center justify-content-center w-100" style={{ flex: '0 0 10%', padding: '0.4rem 2rem', whiteSpace: 'nowrap', fontSize: "13px" }} color="primary" onClick={toggleHandleImagesModal}>
                    <i className="ri-image-edit-line me-1"></i>
                    Handle Images
                  </Button>
                  <Button
                    className="d-flex align-items-center justify-content-center w-100"
                    style={{ flex: '0 0 10%', padding: '0.4rem 2rem', whiteSpace: 'nowrap', fontSize: "13px" }}
                    color={isLimitReached ? "secondary" : "warning"}
                    onClick={toggleBulkAddModal}
                    id="bulk-add-btn"
                  >
                    <i className="ri-file-list-3-line me-1"></i>
                    Bulk Add Items
                  </Button>
                  {isLimitReached && (
                    <UncontrolledTooltip target="bulk-add-btn">
                      {t("Limit reached. Upgrade to add more.")}
                    </UncontrolledTooltip>
                  )}
                </div>
              )}
            

            </div>
            <div className="d-flex align-items-center gap-2 w-100 my-2">
                  <h6 className="text-nowrap mb-0">Search: </h6>
                  <div className="flex-grow-1">
                    <SearchBar
                      onSearch={searchHandler}
                      placeholder="Search menu items by name or description..."
                    />
                  </div>
                </div>
          </div>
        }
        data={currenItems}
        columns={columns}
        loading={isLoading}
        paginationTotalRows={totalRows}
        onChangePage={onChangePage}
        onHeaderAddBtnClick={null}

        onHeaderDeleteBtnClick={() => {
          alert("Soon");
        }}
        showSearch={false}

        actionColWidth="100px"
        showActionColumn={canEditMenuItem}
        showSubHeader={false}
        showActionButtons={canEditMenuItem}
        customActionBtns={(row) => (
          <>
            <button
              className="btn btn-sm btn-warning"
              onClick={() => {
                toggleEditItemModal(row);
              }}
              title="Edit"
            >
              <i className="ri-edit-fill"></i>{" "}
            </button>

            <button
              className="btn btn-sm btn-danger"
              onClick={() => {
                showConfirm(
                  () => {
                    deleteItemFun(row?.item_id);
                  },
                  () => {
                    console.log("Cancelled");
                  },
                  "Confirmation", // Title of the confirmation dialog
                  `Are you sure you want to delete the menu Item ${row?.title} (id: ${row?.item_id})?` // Question displayed in the dialog
                );
              }}
              title="Delete"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </>
        )}
      >
      </DataTableBase >

      {showADModal && canEditMenuItem && (
        <ADModal
          menuId={menuid}
          toggleADModal={toggleADModal}
          showADModal={showADModal}
        />
      )}
      {
        showAddItemModal && (
          <AddItem
            toggleAddItemModal={toggleAddItemModal}
            showAddItemModal={showAddItemModal}
            menuId={menuid}
          />
        )
      }

      {
        selectedRow && (
          <EditItem
            toggleEditItemModal={toggleEditItemModal}
            showEditItemModal={showEditItemModal}
            rowData={selectedRow}
            image_url={image_url}
            menuId={menuid}
          />
        )
      }
      {
        showOptionsModal && (
          <OptionsModal
            isOpen={showOptionsModal}
            toggle={() => setShowOptionsModal(!showOptionsModal)}
            toggleEdit={showEditItemModal}
            itemId={selectedOptions}
          />
        )
      }
      {
        showOrderingModal && canEditMenuItem && (
          <OrderingModal
            isOpen={showOrderingModal}
            toggle={toggleOrderingModal}
            menuId={menuid}
            onOrderingUpdate={handleOrderingUpdate}
          />
        )
      }
      {
        showHandleImagesModal && canEditMenuItem && (
          <HandleImagesModal
            isOpen={showHandleImagesModal}
            toggle={toggleHandleImagesModal}
            menuId={menuid}
            imageUrl={image_url}
          />
        )
      }
      {showBulkAddModal && canEditMenuItem && (
        <BulkAddItemsModal
          isOpen={showBulkAddModal}
          toggle={toggleBulkAddModal}
          menuId={menuid}
          imageUrl={image_url}
          onSuccess={() => {
            getItems(menuid, 1, 10, searchTerm);
          }}
        />
      )}
      <PlanUpgradeModal
        isOpen={showUpgradeModal}
        toggle={toggleUpgradeModal}
      />
    </>
  );
};

export default Menu;
